import { cn } from '../../lib/utils';
import { Button } from '../ui';

/**
 * EmptyState
 *
 * Placeholder component for empty lists and search results.
 * Follows Raflr design system.
 */
export default function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  actionLabel,
  onAction,
  secondaryAction,
  secondaryActionLabel,
  onSecondaryAction,
  className,
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center py-12 px-4',
        className
      )}
    >
      {/* Icon */}
      {Icon && (
        <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-primary/10 border border-primary/20 mb-4">
          <Icon className="h-8 w-8 text-primary" />
        </div>
      )}

      {/* Title */}
      {title && (
        <h3 className="text-lg font-display font-semibold text-cream mb-2">
          {title}
        </h3>
      )}

      {/* Description */}
      {description && (
        <p className="text-cream/60 max-w-sm mb-6">
          {description}
        </p>
      )}

      {/* Actions */}
      {(action || onAction) && (
        <div className="flex flex-wrap items-center justify-center gap-3">
          <Button onClick={onAction}>
            {actionLabel || action}
          </Button>

          {(secondaryAction || onSecondaryAction) && (
            <Button variant="outline" onClick={onSecondaryAction}>
              {secondaryActionLabel || secondaryAction}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * NoRafflesEmpty
 *
 * Pre-configured empty state for no raffles.
 */
export function NoRafflesEmpty({ onCreateRaffle }) {
  return (
    <EmptyState
      icon={TicketIcon}
      title="No competitions yet"
      description="Be the first to create a competition!"
      actionLabel="Create a Competition"
      onAction={onCreateRaffle}
    />
  );
}

/**
 * NoSearchResultsEmpty
 *
 * Pre-configured empty state for no search results.
 */
export function NoSearchResultsEmpty({ searchTerm, onClearSearch }) {
  return (
    <EmptyState
      icon={SearchIcon}
      title="No results found"
      description={`We couldn't find any competitions matching "${searchTerm}". Try a different search or browse all competitions.`}
      actionLabel="Clear search"
      onAction={onClearSearch}
    />
  );
}

/**
 * NoEntriesEmpty
 *
 * Pre-configured empty state for no entries.
 */
export function NoEntriesEmpty({ onBrowse }) {
  return (
    <EmptyState
      icon={TicketIcon}
      title="No entries yet"
      description="You haven't entered any competitions. Browse available competitions and test your skill!"
      actionLabel="Browse Competitions"
      onAction={onBrowse}
    />
  );
}

// Simple icon components
function TicketIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"
      />
    </svg>
  );
}

function SearchIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
      />
    </svg>
  );
}
